import api, { formatApiErrorDetail } from "@/lib/api";
import { normalizeScan } from "@/lib/scan";

// ONE IMPLEMENTATION FOR EVERY BOX THAT ASKS FOR A PART TAG.
//
// WHY THIS EXISTS (his report, 2026-10-07): creating a child off a part and scanning the
// tag's QR filled the box with "a bunch of stuff instead of the tag number", and he had to
// delete it and type the number by hand. Two faults, one after the other:
//   1. A printed tag's QR holds a URL — {site}/scan/part/<token> — so a scan gun typed the
//      whole link into the box. `normalizeScan` strips that to the token.
//   2. The token is NOT the tag number. It is a random 27-character key, and the screens
//      then uppercased it, which breaks a case-sensitive token outright.
// So extraction alone is not enough: the box has to end up holding FAS-000123, the number
// printed on the tag in front of him. That canonicalisation is ONE server-side rule
// (`canonical_tag_code` + the tag's own record), so it is asked for rather than
// re-implemented per screen — a second copy of the rule is how screens start disagreeing.
export async function resolveScannedTag(raw) {
  const value = normalizeScan(raw);
  if (!value) return { code: "", error: "Scan or type a tag first." };
  try {
    const { data } = await api.get("/tags/resolve", { params: { value } });
    return {
      code: data.code,                      // always FAS-000123, whatever was scanned
      status: data.status,                  // fresh / printed / bound / voided / retired
      boundPart: data.bound_part || null,    // already on a part? say so before submitting
      scannedAUrl: !!data.scanned_a_url,
      error: "",
    };
  } catch (e) {
    return { code: "", error: formatApiErrorDetail(e.response?.data?.detail) || "Couldn't read that tag." };
  }
}

// One sentence a screen can show, so the wording of "that tag is spoken for" is also
// written once instead of five times.
export function tagStatusWords(r) {
  if (!r || !r.code) return "";
  if (r.boundPart) {
    return r.boundPart.archived
      ? `${r.code} was retired with part ${r.boundPart.code} — it can never be reused.`
      : `${r.code} is already on part ${r.boundPart.code}.`;
  }
  if (r.status === "voided" || r.status === "retired") return `${r.code} is ${r.status} — use a fresh tag.`;
  return `${r.code} — free to use.`;
}
